import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import { clsxm } from '@vs-shop/utils';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import { FramedMedia, type FramedMediaProps } from '../../internal/framedMedia.tsx';
import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The grid of a category's children: a picture, a name and a product count per
 * tile, the whole tile a link. Flat named parts a tenant arranges in its own
 * JSX, each painting itself.
 *
 *   const { isExpanded, toggle, gridRef, hasHiddenTiles } =
 *     useSubcategoryDisclosure({ count: taxons.length, limits });
 *
 *   <SubcategoryGridTitle render={<Heading level={2} />}>{title}</SubcategoryGridTitle>
 *
 *   <SubcategoryGrid ref={gridRef} aria-label={navLabel}>
 *     <SubcategoryGridList>
 *       {taxons.map((taxon, index) => (
 *         <SubcategoryGridItem key={taxon.code} index={index} limits={limits} expanded={isExpanded}>
 *           <SubcategoryGridTile href={taxon.href} title={taxon.title}>
 *             <SubcategoryGridTileLabel>
 *               <SubcategoryGridThumb src={taxon.image} alt={taxon.title} width={71} height={53} />
 *               <SubcategoryGridName>{taxon.name}</SubcategoryGridName>
 *             </SubcategoryGridTileLabel>
 *             <SubcategoryGridCount>{taxon.total}</SubcategoryGridCount>
 *           </SubcategoryGridTile>
 *         </SubcategoryGridItem>
 *       ))}
 *     </SubcategoryGridList>
 *   </SubcategoryGrid>
 *
 *   {hasHiddenTiles && (
 *     <SubcategoryGridDisclosure count={taxons.length} limits={limits}>
 *       <SubcategoryGridToggle aria-expanded={isExpanded} onClick={toggle}>{label}</SubcategoryGridToggle>
 *     </SubcategoryGridDisclosure>
 *   )}
 *
 * No `variant`: a grid of subcategories paints the same wherever it is placed,
 * and what differs between tenants is how many tiles each step shows — which is
 * data, not an appearance. Where the grid SITS, and what sits behind it, is page
 * assembly and stays in tenant JSX; nothing here renders a container.
 *
 * Server-first: every part is a plain server component. The disclosure's state
 * lives in {@link useSubcategoryDisclosure}, which a tenant calls from its own
 * client composition — so a grid rendered without one costs no client
 * JavaScript at all.
 */

/**
 * How many tiles the collapsed grid shows at each step of the breakpoint
 * contract. The numbers are the TENANT's — how many tiles fit is a question
 * about that brand's column widths — so they arrive as a prop and are never
 * constants here.
 *
 * Named for the contract steps the classes below key off, so a limit and the
 * breakpoint it caps cannot drift apart.
 */
export type SubcategoryGridLimits = {
  readonly pho: number;
  readonly tab: number;
  readonly lap: number;
  readonly des: number;
};

export type SubcategoryGridProps = ComponentProps<'nav'>;

/**
 * The navigation region the tiles sit in. A `<nav>` because a grid of links
 * into a category's children IS a way around the shop; the label naming it is
 * the tenant's, since only a tenant knows what its taxonomy is called.
 *
 * Carries no recipe: the grid itself is the list's, and this contributes the
 * landmark and the element {@link useSubcategoryDisclosure} scrolls back to.
 */
export const SubcategoryGrid = ({ className, ...props }: SubcategoryGridProps) => (
  <nav data-slot="subcategory-grid" className={optionalClassName(clsxm(className))} {...props} />
);

/**
 * What the grid is called. The scale steps up once the tiles get their second
 * row of type.
 *
 * Stated here rather than taken from `headingVariants`, which is what
 * `ListingHeaderTitle` does: a listing's title IS the heading primitive's first
 * level, so restating it there would have been a copy waiting to drift. This
 * scale matches no level of that primitive — it sits between its third and
 * second — because a section title mid-document is furniture rather than the
 * page's opening line. There is nothing to take, so it is declared.
 */
const subcategoryGridTitleClasses = 'lap:text-[26px]/[28px] text-[18px]/[27px]';

export type SubcategoryGridTitleProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the default `<div>` — the one place a page
   * decides whether this text is a heading, and at what level. A category page
   * placing the grid mid-document hands it an `<h2>`; a page that repeats the
   * grid, or nests it, may want no heading at all.
   *
   * Declared by what this part reads off the element rather than by a tag, so
   * any element with a `className` qualifies.
   */
  readonly render?: RenderElement;
};

/** The grid's title. Carries the type scale; the element is the page's to choose. */
export const SubcategoryGridTitle = ({
  className,
  render,
  ...props
}: SubcategoryGridTitleProps) => (
  <RenderedPart
    dataSlot="subcategory-grid-title"
    className={clsxm(subcategoryGridTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The tiles themselves, in as many columns as the step has room for. The
 * column counts are the section's and the tile COUNTS are the tenant's: how
 * wide a tile is follows from the tile, how many of them are worth showing
 * before the fold does not.
 */
const subcategoryGridListClasses =
  'tab:grid-cols-3 lap:grid-cols-4 des:grid-cols-5 grid grid-cols-2 gap-2';

export type SubcategoryGridListProps = ComponentProps<'ul'>;

/** The tile grid. */
export const SubcategoryGridList = ({ className, ...props }: SubcategoryGridListProps) => (
  <ul
    data-slot="subcategory-grid-list"
    className={clsxm(subcategoryGridListClasses, className)}
    {...props}
  />
);

export type SubcategoryGridItemClassParameters = {
  /** The tile's position in the grid, from which every cap below is read. */
  readonly index: number;
  readonly limits: SubcategoryGridLimits;
  /** Whether the grid is showing everything, in which case nothing is capped. */
  readonly expanded: boolean;
};

/**
 * Whether the collapsed grid holds this tile back, one arm per contract step.
 *
 * A function rather than a `cva`: the arms are not an appearance a caller picks
 * but a comparison of the tile's index against four numbers, and the expanded
 * grid emits no class at all rather than the false arm of each pair. Every
 * class is still a literal, because Tailwind generates what it can read.
 *
 * The tile is HIDDEN, not dropped. A tile that is not rendered cannot be
 * revealed without a round trip, and the whole point of the cap is that
 * expanding it is instant.
 */
export const subcategoryGridItemClasses = ({
  index,
  limits,
  expanded,
}: SubcategoryGridItemClassParameters): string =>
  clsxm({
    hidden: !expanded && index >= limits.pho,
    'tab:block': !expanded && index < limits.tab,
    'tab:hidden': !expanded && index >= limits.tab,
    'lap:block': !expanded && index < limits.lap,
    'lap:hidden': !expanded && index >= limits.lap,
    'des:block': !expanded && index < limits.des,
    'des:hidden': !expanded && index >= limits.des,
  });

export type SubcategoryGridItemProps = ComponentProps<'li'> & SubcategoryGridItemClassParameters;

/** One tile's cell, shown or held back at each step according to the tenant's limits. */
export const SubcategoryGridItem = ({
  className,
  index,
  limits,
  expanded,
  ...props
}: SubcategoryGridItemProps) => {
  // Empty whenever the grid is expanded, and dropped rather than stamped as
  // `class=""` on every cell of the grid.
  const resolved = clsxm(subcategoryGridItemClasses({ index, limits, expanded }), className);

  return (
    <li data-slot="subcategory-grid-item" className={optionalClassName(resolved)} {...props} />
  );
};

/**
 * The tile's box, and the whole of it is the click target.
 *
 * `group` is what the name below reads its hover underline through, so the
 * affordance follows the tile rather than the text the pointer happens to be
 * over.
 */
const subcategoryGridTileClasses =
  'border-foreground/10 bg-background lap:h-16 group flex h-12 items-center gap-2 rounded-sm border p-2 transition-colors';

export type SubcategoryGridTileProps = VsLinkProps;

/**
 * One subcategory, as the link into it.
 *
 * The dedicated link part rather than an anchor hand-wrapped around the tile at
 * the call site: whole-surface click is the same decision in every tenant, and
 * a part is where its ARIA contract can be stated once. It is named by what it
 * CONTAINS — the subcategory's name is inside the anchor — so it needs no
 * `aria-label`; a tenant whose tile is not self-naming passes one, and it rides
 * the native prop spread.
 *
 * Renders through the `Link` seam, so a host that aliases the specifier keeps
 * client-side navigation and one that does not resolves a plain `<a>`.
 */
export const SubcategoryGridTile = ({ className, ...props }: SubcategoryGridTileProps) => (
  <Link
    data-slot="subcategory-grid-tile"
    className={clsxm(subcategoryGridTileClasses, className)}
    {...props}
  />
);

/** What identifies the subcategory — its picture and its name, read together. */
const subcategoryGridTileLabelClasses = 'flex items-center gap-2';

export type SubcategoryGridTileLabelProps = ComponentProps<'div'>;

/** The tile's picture and name, as one group at the leading edge. */
export const SubcategoryGridTileLabel = ({
  className,
  ...props
}: SubcategoryGridTileLabelProps) => (
  <div
    data-slot="subcategory-grid-tile-label"
    className={clsxm(subcategoryGridTileLabelClasses, className)}
    {...props}
  />
);

/**
 * The frame the tile's picture sits in — a fixed height at a fixed ratio, so a
 * row of tiles holds one baseline whatever shape the pictures are. Shared with
 * {@link SubcategoryGridThumbPlaceholder} so the two occupy the same box.
 */
const subcategoryGridThumbFrameClasses =
  'lap:h-[53px] flex aspect-[4/3] h-[28px] shrink-0 items-center';

/** The picture itself: contained, never cropped — a category's mark is a subject, not a texture. */
const subcategoryGridThumbImageClasses = 'h-full w-full object-contain';

/**
 * A subcategory's picture. `className` lands on the frame, as
 * {@link SubcategoryGridThumbPlaceholder}'s does, so the two stay
 * interchangeable in the same box.
 */
export type SubcategoryGridThumbProps = FramedMediaProps;

/** The subcategory's picture, contained in its frame. */
export const SubcategoryGridThumb = ({ className, ...props }: SubcategoryGridThumbProps) => (
  <FramedMedia
    dataSlot="subcategory-grid-thumb"
    frameClassName={clsxm(subcategoryGridThumbFrameClasses, className)}
    imageClassName={subcategoryGridThumbImageClasses}
    {...props}
  />
);

export type SubcategoryGridThumbPlaceholderProps = ComponentProps<'div'>;

/**
 * The same frame with no picture in it — for a subcategory whose imagery is
 * missing before anything is requested. What goes inside is the tenant's; this
 * package ships only the box, so whatever stands in centres where the picture
 * would have.
 */
export const SubcategoryGridThumbPlaceholder = ({
  className,
  ...props
}: SubcategoryGridThumbPlaceholderProps) => (
  <div
    data-slot="subcategory-grid-thumb-placeholder"
    className={clsxm(subcategoryGridThumbFrameClasses, className)}
    {...props}
  />
);

/**
 * The subcategory's name. Two lines, then it clamps — a tile is a fixed height,
 * and a third line would push the picture beside it out of the box.
 */
const subcategoryGridNameClasses =
  'lap:text-base line-clamp-2 text-sm underline-offset-2 group-hover:underline';

export type SubcategoryGridNameProps = ComponentProps<'div'>;

/** The subcategory's name. */
export const SubcategoryGridName = ({ className, ...props }: SubcategoryGridNameProps) => (
  <div
    data-slot="subcategory-grid-name"
    className={clsxm(subcategoryGridNameClasses, className)}
    {...props}
  />
);

/**
 * How many products the subcategory holds, at the trailing edge of the tile.
 *
 * `ml-auto` is the count's own place rather than spacing the tile hands it: a
 * tile composed with no count still reads its picture and name from the leading
 * edge, where `justify-between` over exactly two children would have left them
 * spread across the box.
 *
 * Withheld until the laptop step, where the tile is finally wide enough that a
 * number does not compete with the name for the same line.
 */
const subcategoryGridCountClasses = 'text-foreground-muted lap:block ml-auto hidden';

export type SubcategoryGridCountProps = ComponentProps<'span'>;

/** The subcategory's product count. */
export const SubcategoryGridCount = ({ className, ...props }: SubcategoryGridCountProps) => (
  <span
    data-slot="subcategory-grid-count"
    className={clsxm(subcategoryGridCountClasses, className)}
    {...props}
  />
);

export type SubcategoryGridDisclosureClassParameters = {
  /** How many tiles the grid holds in total. */
  readonly count: number;
  readonly limits: SubcategoryGridLimits;
};

/**
 * Whether the control that expands the grid is worth showing at each step.
 *
 * The arms are asymmetric on purpose. The row is `flex` to begin with, so a
 * step only needs `:flex` where a SMALLER step may have hidden it — which is
 * every step but the tablet one, the first above the base. Hiding is what each
 * step decides for itself: a grid whose every tile already fits at this width
 * has nothing left to expand, and a control that says so is a lie.
 *
 * A function rather than a `cva` for the same reason as
 * {@link subcategoryGridItemClasses}: the arms compare a count against the
 * tenant's numbers rather than naming an appearance.
 */
export const subcategoryGridDisclosureClasses = ({
  count,
  limits,
}: SubcategoryGridDisclosureClassParameters): string =>
  clsxm('flex w-full justify-center pt-4', {
    'tab:hidden': count <= limits.tab,
    'lap:flex': count > limits.lap,
    'lap:hidden': count <= limits.lap,
    'des:flex': count > limits.des,
    'des:hidden': count <= limits.des,
  });

export type SubcategoryGridDisclosureProps = ComponentProps<'div'> &
  SubcategoryGridDisclosureClassParameters;

/**
 * The row the expand control sits in, present only at the steps that are
 * actually hiding something. Whether it is composed AT ALL is the tenant's,
 * from {@link useSubcategoryDisclosure}'s `hasHiddenTiles`.
 */
export const SubcategoryGridDisclosure = ({
  className,
  count,
  limits,
  ...props
}: SubcategoryGridDisclosureProps) => (
  <div
    data-slot="subcategory-grid-disclosure"
    className={clsxm(subcategoryGridDisclosureClasses({ count, limits }), className)}
    {...props}
  />
);

/** The control that expands the grid, painted as a line of text rather than as a button. */
const subcategoryGridToggleClasses =
  'text-foreground hover:text-foreground/80 text-sm underline underline-offset-2';

export type SubcategoryGridToggleProps = ComponentProps<'button'>;

/**
 * The expand control. Which state it is in arrives as `aria-expanded` and as
 * nothing else — the attribute a screen reader announces is already in the
 * markup, so the part takes no `isExpanded` prop that could disagree with it —
 * and what it says arrives as children, because "show all" and "show fewer" are
 * a tenant's words.
 */
export const SubcategoryGridToggle = ({ className, ...props }: SubcategoryGridToggleProps) => (
  <button
    type="button"
    data-slot="subcategory-grid-toggle"
    className={clsxm(subcategoryGridToggleClasses, className)}
    {...props}
  />
);
